import { useId } from 'react';
import { NJFormItem, NJIconButton, NJText, NJTooltip } from '@engie-group/fluid-design-system-react';
import { fmt } from './domain';

/** Les degrés-jours s'affichent sans unité : ce sont des nombres de référence. */
function DegreeBase({ base }: { base: number }) {
  return <NJText scale="sm">Base {base} °C</NJText>;
}

function ReferenceSource({ source }: { source: string }) {
  const tooltipId = useId();
  return (
    <NJTooltip id={tooltipId} placement="bottom" text={<NJText scale="sm">{source}</NJText>}>
        <NJIconButton icon="info" scale="xs" ariaDescribedby={tooltipId} label="Source des ratios de référence" />
    </NJTooltip>
  );
}

export function ReferenceClimate({ zone }: { zone?: string }) {
  return (
    <div className="pcv3-degree-figure pcv3-reference-climate" aria-label="Zone climatique de référence">
      <NJText scale="lg" className="pcv3-degree-number">{zone ?? 'Indéterminée'}</NJText>
      <NJText scale="sm">Zone de référence</NJText>
    </div>
  );
}

function Figure({ label, value, highlight, base }: { label: string; value: number; highlight?: boolean; base?: number }) {
  return (
    <div className="pcv3-degree-figure">
      <div className="pcv3-degree-value">
      <NJText as="span" scale="sm">{label}</NJText>
      <NJText as="span" scale="lg" className={highlight ? 'pcv3-degree-number pcv3-ratio-changed' : 'pcv3-degree-number'}>
        {fmt(value)}
      </NJText>
      </div>
      {base !== undefined && <DegreeBase base={base} />}
    </div>
  );
}

/** Couple DJU / DJClim en lecture, groupé pour ne pas sembler lié aux tableaux voisins. */
export function DegreePair({ dju, djclim, hint, highlight, climateBases, reference }: {
  dju: number; djclim: number; hint?: string; highlight?: boolean; climateBases?: boolean; reference?: { zone?: string; source: string };
}) {
  return (
    <div className={`pcv3-degree-pair${highlight ? ' pcv3-degree-pair-changed' : ''}`}>
      {reference && <div className="pcv3-degree-group-title">
        <NJText scale="sm">{hint}</NJText>
        <ReferenceSource source={reference.source} />
      </div>}
      <div className="pcv3-degree-figures">
        <Figure label="DJU" value={dju} highlight={highlight} base={climateBases ? 18 : undefined} />
        <Figure label="DJClim" value={djclim} highlight={highlight} base={climateBases ? 15 : undefined} />
        {reference && <ReferenceClimate {...reference} />}
      </div>
      {!reference && hint && <NJText scale="sm">{hint}</NJText>}
    </div>
  );
}

/** Degré-jour en saisie, sans unité, avec plage attendue et état d'erreur. */
export function DegreeField({ label, text, valid, range, onChange, base }: {
  label: string; text: string; valid: boolean; range: string; onChange: (v: string) => void; base?: number;
}) {
  const id = useId();
  return (
    <div className="pcv3-degree-field">
      <NJFormItem id={id} label={label} labelKind="floating" type="text" inputMode="decimal"
        value={text} hasError={!valid} subscriptMessage={valid ? range : `Valeur invalide : ${range}`}
        onChange={(event) => {
          if (event.target instanceof HTMLInputElement) onChange(event.target.value);
        }} />
      {base !== undefined && <DegreeBase base={base} />}
    </div>
  );
}

/** Deux champs de degrés-jours côte à côte, groupés comme en lecture. */
export function DegreePairFields({ children, reference }: { children: React.ReactNode; reference?: { zone?: string; source: string } }) {
  return (
    <div className="pcv3-degree-pair">
      {reference && <div className="pcv3-degree-group-title">
        <NJText scale="sm">Degrés-jours de mes ratios</NJText>
        <ReferenceSource source={reference.source} />
      </div>}
      <div className="pcv3-degree-fields">
      {children}
      </div>
    </div>
  );
}
